export type DecimalString = string;

export type BudgetCategory =
  | "salary"
  | "taxes"
  | "rent"
  | "supplies"
  | "fixed_assets"
  | "software"
  | "gph"
  | "msb_services"
  | "other_services";

export type BudgetType = "admin" | "mto" | "direct";

export type FailureType =
  | "insufficient_remaining"
  | "initial_cash_exhausted"
  | "sum_mismatch"
  | "no_sources"
  | "bad_source"
  | "invalid_input";

export interface ChiefOverrideFields {
  override_reason: string;
}

export interface BudgetLimitErrorResponseV2 {
  error: string;
  failure_type: FailureType;
  is_overrideable: boolean;
  section: string;
  limit: DecimalString | null;
  spent: DecimalString | null;
  requested: DecimalString | null;
  available: DecimalString | null;
  project_id: number | null;
}

export interface SmetaCategoryTotals {
  planned: DecimalString;
  allocated: DecimalString;
  spent: DecimalString;
  remaining: DecimalString;
  type: BudgetType;
}

export interface DonorSmetaResponse {
  project_id: number;
  project_name_ru: string;
  is_default: false;
  budget: DecimalString;
  categories: Record<BudgetCategory, SmetaCategoryTotals>;
  total_allocated: DecimalString;
  total_spent: DecimalString;
  total_remaining: DecimalString;
}

export type SmetaResponse = DonorSmetaResponse;

export function isDonorSmeta(s: SmetaResponse): s is DonorSmetaResponse {
  return s.is_default === false;
}

export interface InsufficientPotError {
  error: "insufficient_dotted_pot_budget";
  available: string;
  requested: string;
}

export function isInsufficientPotError(error: unknown): error is { data: InsufficientPotError } {
  return (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as Record<string, unknown>).data === "object" &&
    (error as Record<string, unknown>).data !== null &&
    ((error as Record<string, Record<string, unknown>>).data).error === "insufficient_dotted_pot_budget"
  );
}

export type PayrollBudgetCategory = "salary" | "taxes";

export interface RentalSplitV2 {
  project_id: number;
  amount: DecimalString;
}

export interface RentalApproveV2Body {
  splits?: RentalSplitV2[];
  override_reason?: string;
  note?: string;
}

export type AssetType = "fixed_asset" | "inventory" | "intangible";
export type FixedAssetGroup = "group1" | "group2" | "group3" | "group4" | "other";
export type InventorySubtype = "consumable" | "reusable";
export type InKindUnit = "pcs" | "kg" | "m" | "m2" | "m3" | "l";

export interface InKindDonationItem {
  name: string;
  asset_type: AssetType;
  quantity: number;
  unit_price: DecimalString;
  description?: string;
  unit?: InKindUnit;
  fixed_asset_group?: FixedAssetGroup;
  inventory_subtype?: InventorySubtype;
  useful_life_months?: number;
  depreciation_rate?: DecimalString;
}

export interface InKindDonationCreateRequest {
  donor_name: string;
  donor_bin?: string;
  document_number: string;
  document_date: string;
  description?: string;
  project_id?: number;
  items: InKindDonationItem[];
}

export interface InKindDonationCreateResponse {
  income_record: {
    id: number;
  };
  inventory_batches: {
    id: number;
    name: string;
    asset_type: AssetType;
    account_code: "1330" | "2410" | "2730";
    total_cost: DecimalString;
  }[];
  journal_entry_ids: number[];
  invoice_id: number | null;
}

export function isBudgetLimitErrorV2(error: unknown): error is { data: BudgetLimitErrorResponseV2 } {
  return (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as Record<string, unknown>).data === "object" &&
    (error as Record<string, unknown>).data !== null &&
    typeof ((error as Record<string, Record<string, unknown>>).data).failure_type === "string"
  );
}

export function isOverrideableError(error: BudgetLimitErrorResponseV2): boolean {
  return error.is_overrideable === true;
}
